import { Component } from 'react';
import { localeFromPath, preferredLocale } from '../i18n/locales';
import { track } from '../analytics/track';
import { isChunkLoadError, isReloadingForNewBuild, pagePath, reloadForNewBuild } from '../utils/staleBuild';

// This sits above the router and the locale provider, so it carries its own words
const COPY = {
  en: {
    title: 'Something went wrong on this page',
    body: 'Your projects are safe. Reloading usually fixes it.',
    reload: 'Reload the page',
    home: 'Go to the home page',
  },
  es: {
    title: 'Algo salió mal en esta página',
    body: 'Tus proyectos están a salvo. Recargar suele solucionarlo.',
    reload: 'Recargar la página',
    home: 'Ir al inicio',
  },
  pt: {
    title: 'Algo deu errado nesta página',
    body: 'Seus projetos estão seguros. Recarregar costuma resolver.',
    reload: 'Recarregar a página',
    home: 'Ir para o início',
  },
  ar: {
    title: 'حصلت مشكلة في الصفحة دي',
    body: 'مشاريعك في أمان. إعادة تحميل الصفحة غالبًا بتحل المشكلة.',
    reload: 'حمّل الصفحة تاني',
    home: 'روح للصفحة الرئيسية',
  },
  fr: {
    title: 'Un problème est survenu sur cette page',
    body: 'Vos projets sont en sécurité. Recharger la page règle généralement le problème.',
    reload: 'Recharger la page',
    home: 'Aller à l’accueil',
  },
  de: {
    title: 'Auf dieser Seite ist etwas schiefgelaufen',
    body: 'Deine Projekte sind sicher. Meist hilft es, die Seite neu zu laden.',
    reload: 'Seite neu laden',
    home: 'Zur Startseite',
  },
};

const copyFor = () => {
  try {
    return COPY[localeFromPath(window.location.pathname) || preferredLocale()] || COPY.en;
  } catch {
    return COPY.en;
  }
};

// Browser extensions (Google Translate, password managers, ad blockers) that
// move React-managed DOM nodes: re-rendering recovers from these
export const isDomReconciliationError = (error) =>
  error?.message?.includes('removeChild') ||
  error?.message?.includes('insertBefore') ||
  error?.message?.includes('appendChild') ||
  error?.name === 'NotFoundError';

// The first React component in the stack, which is the one that threw: with
// only the error's name recorded, three crash pages in one evening said
// "TypeError" and nothing else, and none of them could be chased
export const crashingComponent = (componentStack) => {
  const match = /\n\s*(?:at|in)\s+([A-Za-z0-9_$.]+)/.exec(componentStack || '');
  return match ? match[1] : null;
};

export function CrashFallback() {
  const copy = copyFor();
  return (
    <div role="alert" style={{
      minHeight: '100vh', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center',
      gap: 16, padding: '0 16px', textAlign: 'center', background: '#09090b', color: '#fafafa',
      fontFamily: 'Geist, system-ui, -apple-system, sans-serif',
    }}>
      <h1 style={{ fontSize: 20, fontWeight: 600, margin: 0 }}>{copy.title}</h1>
      <p style={{ fontSize: 14, color: '#a1a1aa', margin: 0, maxWidth: 360 }}>{copy.body}</p>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 8, width: '100%', maxWidth: 280 }}>
        <button
          type="button"
          onClick={() => window.location.reload()}
          style={{ height: 44, borderRadius: 8, border: 0, background: '#fafafa', color: '#09090b', fontWeight: 600, fontSize: 15, cursor: 'pointer' }}
        >
          {copy.reload}
        </button>
        <a href="/" style={{ height: 44, lineHeight: '44px', borderRadius: 8, color: '#fafafa', fontSize: 14, textDecoration: 'none' }}>{copy.home}</a>
      </div>
    </div>
  );
}

/**
 * The app's last line. A DOM reconciliation error from an extension is
 * re-rendered away (up to 3 times). Anything else used to be rethrown, which
 * left a blank page (PostHog: six errors on one AI Agent project, from someone
 * who came back 35 minutes later and hit them again); it now shows a way out
 * and is reported.
 */
class ErrorBoundary extends Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false, fatal: false, errorCount: 0 };
  }

  static getDerivedStateFromError(error) {
    // A page chunk from before a deploy: reload onto the new build (utils/staleBuild)
    if (isChunkLoadError(error)) return { hasError: true, fatal: false, reloading: true };
    return { hasError: true, fatal: !isDomReconciliationError(error) };
  }

  componentDidCatch(error, errorInfo) {
    if (isChunkLoadError(error)) {
      // The event for this is sent after the reload, from staleBuild: one sent
      // here would never leave the browser
      if (reloadForNewBuild(Date.now(), 'chunk', error)) return;
      // Vite's preload handler started this very reload a moment ago: wait for it
      if (isReloadingForNewBuild()) return;
      // Reloaded moments ago and the chunk is still missing: a real failure
      this.setState({ reloading: false, fatal: true });
    }

    if (this.state.fatal || !isDomReconciliationError(error)) {
      console.error('[ErrorBoundary] Page crashed:', error, errorInfo?.componentStack);
      track('app_crash', {
        name: error?.name || 'Error',
        // Our own error text, cut short: enough to name the bug, not a payload
        message: String(error?.message || '').slice(0, 300),
        component: crashingComponent(errorInfo?.componentStack),
        page: pagePath(),
      });
      return;
    }

    // Log the error for debugging
    console.warn('[ErrorBoundary] Caught DOM reconciliation error:', error.message);

    // Track error count to prevent infinite recovery loops
    this.setState(prev => ({ errorCount: prev.errorCount + 1 }));

    // If we've had too many errors, stop trying to recover
    if (this.state.errorCount >= 3) {
      console.error('[ErrorBoundary] Too many DOM errors, stopping recovery attempts');
      return;
    }

    // Attempt recovery after a brief delay
    setTimeout(() => {
      this.setState({ hasError: false });
    }, 100);
  }

  render() {
    if (this.state.hasError && this.state.reloading) {
      return null;
    }

    if (this.state.hasError && this.state.fatal) {
      return this.props.fallback || <CrashFallback />;
    }

    // If too many errors, show a minimal fallback
    if (this.state.hasError && this.state.errorCount >= 3) {
      return this.props.fallback || <CrashFallback />;
    }

    return this.props.children;
  }
}

export default ErrorBoundary;
